import React from "react";
import { useNavigate } from "react-router-dom";
import { Path } from "@/router";
import { useApolloClient, type DocumentNode } from "@apollo/client";
import Table, {
  buildTwoLineColumn,
  buildIconTextColumn,
  TableHeading,
  useTableHeadingCollapse
} from "@/components/Table";
import type { TableAction, TableHeadingSize, TableHeadingTopSpacing } from "@/components/Table";
import { buildActionColumn } from "@/components/Table/buildActionColumn";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import {
  formatPATLineItems,
  PAT_EXEMPT_TOUR,
  getPATDocumentIconConfig,
  getPATExpirationAlertIconConfig
} from "@/features/person/utils/pat";
import {
  canOpenPatScoreEditor,
  findPatEventRegistrationId,
  findPatEventRegistrantId,
  mapPatActivityRowToEditScoreSubmission,
  type PatScoreEditRow
} from "@/features/person/utils/pat-score-edit";
import { GET_PROSPECT_PROGRAM_QUERY } from "@/pages/people/[pgaId]/person/queries";
import { Person, PlayingAbilityTestStatus } from "@/types";
import { withGlobalEmptyStates } from "@/components/Table/utils";
import { TABLE_ROW_HOVER_CLASSES } from "@/lib/styleHelpers";
import PATActivityModal from "./PATActivityModal";
import { PATSubmission } from "./PATModalContent";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { usePATMutations } from "./usePATMutations";
import { EXCESSIVE_SCORE_THRESHOLD } from "@/pages/events/[eventSlug]/(tabbed)/results/_utils";
import EditScoreModal, {
  type EditPatScoreSubmission
} from "@/pages/events/[eventSlug]/(tabbed)/results/_components/EditScoreModal";
import useCapabilities, { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { PAT_SCORE_EDIT_EVENT_REGISTRANTS_QUERY, PAT_SCORE_EDIT_REGISTRATION_HISTORY_QUERY } from "./queries";

export interface PATActivityTableProps {
  patStatus: PlayingAbilityTestStatus | null | undefined;
  asDetailsPanel?: boolean;
  loading?: boolean;
  enableNavigation?: boolean;
  title?: string;
  body?: string;
  pgaId?: string;
  refetchQueries?: DocumentNode[];
  personId?: string;
  person?: Person;
  pathAwareExpiration?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  expandRequestToken?: number;
  headingSize?: TableHeadingSize;
  topSpacing?: TableHeadingTopSpacing;
}

const editablePATTypes = ["handicap_submission", "tour_exemption", "alternate_event", "combined_events"];

const getCombinedScoreRoundIds = (data: Record<string, any>[]) => {
  const combinedScore = data.find((row) => row.patType === "combined_score");
  if (!combinedScore) return new Set();

  const roundIds = new Set<string>();
  if (combinedScore.roundOneId) roundIds.add(combinedScore.roundOneId);
  if (combinedScore.roundTwoId) roundIds.add(combinedScore.roundTwoId);

  return roundIds;
};

const getTourDisplayText = (tourCode?: string | null): string | null => {
  if (!tourCode) return null;
  return PAT_EXEMPT_TOUR.find((t) => t.value === tourCode)?.displayText || null;
};

const getFormattedNoteText = (row: any): string => {
  if (row.type === "Exemption" && row.tour) {
    const tourDisplayText = getTourDisplayText(row.tour);
    return tourDisplayText ? `${tourDisplayText} - ${row.note}` : row.note;
  }
  if (row.type === "Handicap" && row.handicap) {
    return `${Number(row.handicap)} Handicap - ${row.note}`;
  }
  return row.note;
};

const getDetailsColumnText = (row: any): React.ReactNode => {
  const isHandicap = row.type === "Handicap";
  const num = Number(row.handicap);
  const formatted = Number.isInteger(num) ? num : num;
  const handicapPrefix = isHandicap ? `${formatted} Handicap` : null;
  const totalScore = Number(row.totalScore);
  let primary: React.ReactNode = null;
  if (!isNaN(totalScore) && totalScore >= EXCESSIVE_SCORE_THRESHOLD) {
    primary = <span>{`Excessive Score ${EXCESSIVE_SCORE_THRESHOLD}+`}</span>;
  } else if (row.note) {
    primary = handicapPrefix ? (
      <span>
        {handicapPrefix} - {row.note}
      </span>
    ) : (
      <span>{row.note}</span>
    );
  } else if (handicapPrefix) {
    primary = <span>{handicapPrefix}</span>;
  }

  const extraLines = [row.suspensionDetailsText, row.expirationDetailsText].filter((text): text is string =>
    Boolean(text)
  );
  if (extraLines.length === 0) {
    return primary ?? <span className="opacity-20">--</span>;
  }

  return (
    <span className="flex flex-col">
      {primary}
      {extraLines.map((text) => (
        <span key={text}>{text}</span>
      ))}
    </span>
  );
};

const mapRowToPATSubmission = (row: any): PATSubmission => {
  return {
    id: row.id,
    patType: row.patType,
    date: row.rawDate || row.date,
    name: row.eventName || null,
    roundOneScore: row.roundOneScore || null,
    roundTwoScore: row.roundTwoScore || null,
    targetScore: row.targetScore || null,
    note: row.note || null,
    tour: row.tour || null,
    handicap: row.handicap || null,
    document: row.document || null
  };
};

const PAT_ACTIVITY_LINK_CLASS = "underline underline-offset-2";

const isNavigablePatActivityRow = (row: { slug?: string | null; type?: string }, enableNavigation: boolean) =>
  enableNavigation && !!row.slug && row.type === "Standard PAT";

const getPatActivityPrimaryText = (row: any): string => {
  const isStandardPAT = row.type === "Standard PAT";
  const isAlternatePAT = row.type === "Alternate PAT";
  const isCombinedEvents = row.type === "Combined Events";
  const isExemption = row.type === "Exemption";

  if (isStandardPAT) return row.eventName ? row.eventName : "Standard PAT";
  if (isAlternatePAT) {
    return row.eventName ? `Alternate Event: ${row.eventName}` : "Alternate Event";
  }
  if (isCombinedEvents) {
    return row.eventName ? `Combined Events: ${row.eventName}` : "Combined Events";
  }
  if (isExemption) {
    const tourDisplayText = getTourDisplayText(row.tour);
    return tourDisplayText ? `Exemption: ${tourDisplayText}` : "Exemption";
  }

  return row.eventName || row.type;
};

const getPatActivityPrimaryLabel = (row: any, enableNavigation: boolean): React.ReactNode => {
  const text = getPatActivityPrimaryText(row);
  if (isNavigablePatActivityRow(row, enableNavigation)) {
    return <span className={PAT_ACTIVITY_LINK_CLASS}>{text}</span>;
  }
  return text;
};

export default function PATActivityTable({
  patStatus,
  asDetailsPanel = false,
  loading = false,
  enableNavigation = true,
  title = "PAT Results",
  body = "View all PAT attempts and results associated with this person.",
  pgaId,
  refetchQueries,
  personId,
  person,
  pathAwareExpiration = false,
  collapsible = false,
  defaultExpanded = true,
  expandRequestToken,
  headingSize,
  topSpacing
}: PATActivityTableProps) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { hasPermission } = useCapabilities();
  const canEditPatScore = hasPermission(PERMISSION.editPAT);
  const canViewPersonEvents = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEvents
  });
  const { setMenu, Menu } = useTableRowMenu();
  const { expanded, setExpanded, headingProps } = useTableHeadingCollapse({ defaultExpanded });
  const [editingSubmission, setEditingSubmission] = React.useState<PATSubmission | null>(null);
  const [editingScoreSubmission, setEditingScoreSubmission] = React.useState<EditPatScoreSubmission | null>(null);
  const [openingPatScoreEditor, setOpeningPatScoreEditor] = React.useState(false);
  const [submissionToDelete, setSubmissionToDelete] = React.useState<{ id: string } | null>(null);
  const [addPATActivityModalOpen, setAddPATActivityModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (expandRequestToken != null) setExpanded(true);
  }, [expandRequestToken, setExpanded]);

  const submissions = React.useMemo(() => {
    if (!patStatus) return [];
    return formatPATLineItems(patStatus, {
      pathAwareExpiration,
      person
    });
  }, [patStatus, pathAwareExpiration, person]);

  const { deleteSubmission, loading: deletingSubmission } = usePATMutations({
    pgaId: pgaId || "",
    onSuccess: () => {
      setSubmissionToDelete(null);
    },
    onError: () => {}
  });

  const openPatScoreEditor = React.useCallback(
    async (row: PatScoreEditRow) => {
      if (!pgaId || !row.slug) {
        showErrorSnackbar("Unable to edit submission", {
          subtitle: "This PAT is not linked to an event registration."
        });
        return;
      }

      setOpeningPatScoreEditor(true);
      try {
        let eventRegistrationId: string | undefined;

        if (canViewPersonEvents) {
          const { data: historyData } = await client.query({
            query: PAT_SCORE_EDIT_REGISTRATION_HISTORY_QUERY,
            variables: { pgaId },
            fetchPolicy: "network-only"
          });
          eventRegistrationId =
            findPatEventRegistrationId(historyData?.eventRegistrationHistory ?? [], row.slug) ?? undefined;
        }

        if (!eventRegistrationId) {
          const { data: registrantData } = await client.query({
            query: PAT_SCORE_EDIT_EVENT_REGISTRANTS_QUERY,
            variables: { slug: row.slug, search: pgaId, first: 25 },
            fetchPolicy: "network-only"
          });
          const registrants = (registrantData?.eventRegistrants?.edges ?? []).map((edge) => edge.node);
          eventRegistrationId = findPatEventRegistrantId(registrants, pgaId) ?? undefined;
        }

        if (!eventRegistrationId) {
          showErrorSnackbar("Unable to edit submission", {
            subtitle: "This PAT is not linked to an event registration."
          });
          return;
        }

        setEditingScoreSubmission(mapPatActivityRowToEditScoreSubmission(row, eventRegistrationId));
      } catch (error) {
        showErrorSnackbar("Unable to edit submission", { subtitle: "Please try again later." });
      } finally {
        setOpeningPatScoreEditor(false);
      }
    },
    [canViewPersonEvents, client, pgaId]
  );

  const combinedRoundIds = React.useMemo(() => getCombinedScoreRoundIds(submissions), [submissions]);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        ...buildTwoLineColumn({
          accessorKey: "patActivity",
          header: "PAT Activity",
          getPrimaryText: (row: any) => getPatActivityPrimaryLabel(row, enableNavigation),
          getSecondaryText: (row: any) => row.date,
          size: 300,
          minSize: 200
        }),
        enableSorting: false
      },
      buildIconTextColumn({
        accessorKey: "roundOneScore",
        header: "Rnd 1",
        enableSorting: false,
        getText: (row: any) => row.roundOneScore,
        getIcon: (row: any) => {
          const isCombinedScore = row.type === "Combined Score";
          const isUsedInCombined = !isCombinedScore && row.roundOneId && combinedRoundIds.has(row.roundOneId);
          return isUsedInCombined
            ? {
                name: "check",
                tooltip: {
                  supportingText: "Used in Combined Score"
                }
              }
            : null;
        },
        size: 60
      }),
      buildIconTextColumn({
        accessorKey: "roundTwoScore",
        header: "Rnd 2",
        enableSorting: false,
        getText: (row: any) => row.roundTwoScore,
        getIcon: (row: any) => {
          const isCombinedScore = row.type === "Combined Score";
          const isUsedInCombined = !isCombinedScore && row.roundTwoId && combinedRoundIds.has(row.roundTwoId);
          return isUsedInCombined
            ? {
                name: "check",
                tooltip: {
                  supportingText: "Used in Combined Score"
                }
              }
            : null;
        },
        size: 60
      }),
      {
        accessorKey: "totalScore",
        header: "Total",
        enableSorting: false,
        size: 60
      },
      {
        accessorKey: "targetScore",
        header: "Target",
        enableSorting: false,
        size: 60
      },
      {
        accessorKey: "status",
        header: pathAwareExpiration ? "Result" : "Status",
        enableSorting: false,
        size: 60,
        meta: { headerClassName: "hide-header-in-column" },
        cell: ({ row }: { row: any }) => row.original.status
      },
      buildIconTextColumn({
        accessorKey: "details",
        header: "Details",
        enableSorting: false,
        meta: { skipEmptyState: true },
        getText: getDetailsColumnText,
        getIcon: (row: any) => [
          getPATExpirationAlertIconConfig(row.expirationAlertTooltip),
          getPATDocumentIconConfig(row.hasDocument, row.documentUrl, row.note, getFormattedNoteText(row))
        ]
      }),
      buildActionColumn<any>({
        getActions: (row: any) => {
          const actions: Action[] = [];
          if (canEditPatScore && pgaId && canOpenPatScoreEditor(row)) {
            actions.push({
              text: "Edit PAT Score",
              onClick: () => {
                void openPatScoreEditor(row);
              }
            });
          }

          if (pgaId && editablePATTypes.includes(row.patType)) {
            actions.push({
              text: "Edit PAT Activity",
              onClick: () => {
                setEditingSubmission(mapRowToPATSubmission(row));
              }
            });
          }

          if (row.slug && enableNavigation) {
            const eventDate = new Date(row.rawDate);
            if (eventDate > new Date()) {
              actions.push({
                text: "View PAT Event",
                onClick: () => navigate(`/events/${row.slug}`)
              });
            } else {
              actions.push({
                text: "View PAT Event Results",
                onClick: () => navigate(`/events/${row.slug}/results`)
              });
            }
          }

          if (pgaId && editablePATTypes.includes(row.patType)) {
            actions.push({
              text: "Delete Activity",
              iconName: "delete",
              destructive: true,
              onClick: () => {
                setSubmissionToDelete({ id: row.id });
              }
            });
          }

          return actions;
        },
        isRowNavigable: (row: any) => !!row.slug && enableNavigation,
        actionId,
        setMenu
      })
    ];

    return withGlobalEmptyStates(baseColumns);
  }, [
    combinedRoundIds,
    enableNavigation,
    navigate,
    setMenu,
    pgaId,
    pathAwareExpiration,
    canEditPatScore,
    openPatScoreEditor
  ]);

  const getPatActivityRowLink = React.useCallback(
    (row: { original: any }) => {
      if (!enableNavigation) return null;

      const original: any = row.original;
      if (original.slug && original.type === "Standard PAT") {
        return { to: `/events/${original.slug}/results` as Path };
      }

      return null;
    },
    [enableNavigation]
  );

  const getRowClasses = React.useCallback(
    (row: { original: any }) => {
      if (!enableNavigation) return "cursor-default";

      const original: any = row.original;
      const isClickable = original.slug && original.type === "Standard PAT";
      return isClickable ? `cursor-pointer ${TABLE_ROW_HOVER_CLASSES}` : "cursor-default";
    },
    [enableNavigation]
  );

  const handleDetailsPanelRowClick = React.useCallback(
    (row: { original: any }) => {
      const link = getPatActivityRowLink(row);
      if (link?.to) {
        navigate(link.to);
      }
    },
    [getPatActivityRowLink, navigate]
  );

  const handleDeleteConfirm = () => {
    if (submissionToDelete) {
      deleteSubmission({
        variables: {
          id: submissionToDelete.id
        }
      });
    }
  };

  const deleteActivityDialog = submissionToDelete && (
    <Dialog
      open={true}
      onClosed={() => setSubmissionToDelete(null)}
      headline="Delete Activity"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={() => setSubmissionToDelete(null)} disabled={deletingSubmission}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDeleteConfirm}
            disabled={deletingSubmission}
            data-testid="confirm-delete-activity"
          >
            Delete
          </Button>
        </>
      }
    >
      <p>Are you sure you want to delete this PAT activity? This action cannot be undone.</p>
    </Dialog>
  );

  const headingActions: TableAction[] = React.useMemo(() => {
    if (!personId || !pgaId) return [];
    return [
      {
        label: "Add PAT Activity",
        icon: "add",
        variant: "outlined",
        onClick: () => setAddPATActivityModalOpen(true)
      }
    ];
  }, [personId, pgaId]);

  const showEditPATActivityModal = editingSubmission && pgaId;
  const showAddPATActivityModal = addPATActivityModalOpen && personId && pgaId;

  const handleCloseEditPATScoreModal = () => {
    setEditingScoreSubmission(null);
    if (!pgaId) return;
    void client.refetchQueries({
      include: refetchQueries ?? [GET_PROSPECT_PROGRAM_QUERY]
    });
  };

  const editScoreModal = editingScoreSubmission ? (
    <EditScoreModal onClose={handleCloseEditPATScoreModal} submission={editingScoreSubmission} />
  ) : null;

  const patScoreEditorStatus = (
    <p className="sr-only" role="status" aria-live="polite">
      {openingPatScoreEditor ? "Opening Edit PAT Score" : ""}
    </p>
  );

  if (asDetailsPanel) {
    return (
      <div aria-busy={openingPatScoreEditor || undefined}>
        {patScoreEditorStatus}
        <TableDetailsPanel
          data={submissions}
          columns={columns}
          loading={loading}
          title={`PAT Activity History (${submissions.length})`}
          onClickRow={handleDetailsPanelRowClick}
          getRowClasses={getRowClasses}
        />
        <Menu />
        {showEditPATActivityModal && (
          <PATActivityModal
            onDismiss={() => setEditingSubmission(null)}
            submission={editingSubmission}
            pgaId={pgaId!}
          />
        )}
        {editScoreModal}
        {deleteActivityDialog}
      </div>
    );
  }

  return (
    <div aria-busy={openingPatScoreEditor || undefined}>
      {patScoreEditorStatus}
      <TableHeading
        heading={title}
        body={body}
        actions={headingActions}
        headingSize={headingSize}
        topSpacing={topSpacing}
        {...(collapsible ? headingProps : {})}
      />
      <Table
        data={submissions}
        columns={columns}
        loading={loading}
        getRowLink={getPatActivityRowLink}
        getRowClasses={getRowClasses}
        data-testid="pat-activity-table"
        collapsed={collapsible && !expanded}
      />
      <Menu />
      {showEditPATActivityModal && (
        <PATActivityModal onDismiss={() => setEditingSubmission(null)} submission={editingSubmission} pgaId={pgaId!} />
      )}
      {showAddPATActivityModal && (
        <PATActivityModal onDismiss={() => setAddPATActivityModalOpen(false)} personId={personId!} pgaId={pgaId!} />
      )}
      {editScoreModal}
      {deleteActivityDialog}
    </div>
  );
}
